<template>
  <div>
    <!-- Date Filter -->
    <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
      <div class="flex items-center gap-4">
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700">From:</label>
          <input
            v-model="dateRange.start"
            type="date"
            class="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark"
            @change="fetchMeetings"
          />
        </div>
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-gray-700">To:</label>
          <input
            v-model="dateRange.end"
            type="date"
            class="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark"
            @change="fetchMeetings"
          />
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="px-6 py-4 max-h-96 overflow-y-auto">
      <!-- Loading State -->
      <LoadingIndicator v-if="loading" message="Loading meetings..." class="p-4" />

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-12">
        <div class="max-w-md mx-auto">
          <h3 class="text-lg font-medium text-gray-900 mb-2">Something went wrong</h3>
          <p class="text-gray-600 mb-4">
            {{error}}
          </p>
          <button v-if="errorType !== 'auth'" class="text-sm text-blue-600 hover:text-blue-800" @click="fetchMeetings">
            Try Again
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="displayedMeetings.length === 0 && meetings.length === 0 && !hasEverLoadedSuccessfully" class="text-center py-12">
        <div class="max-w-md mx-auto">
          <div class="text-gray-400 mb-4">
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">No Teams Meetings Found</h3>
          <p class="text-gray-600 mb-4">
            We couldn't find any Teams meetings. This might be because:
          </p>
          <ul class="text-sm text-gray-500 text-left max-w-xs mx-auto mb-6">
            <li class="mb-2">• Your Microsoft account isn't connected</li>
            <li class="mb-2">• You don't have any meetings in this date range</li>
            <li class="mb-2">• You need to refresh your Microsoft permissions</li>
          </ul>
          <button
            @click="fetchMeetings"
            class="text-sm text-blue-600 hover:text-blue-800"
          >
            Try Again
          </button>
        </div>
      </div>

      <!-- Normal Empty State -->
      <div v-else-if="displayedMeetings.length === 0" class="text-center py-12">
        <p class="text-gray-600">{{ emptyStateMessage }}</p>
      </div>

      <!-- Meetings List -->
      <div v-else class="space-y-3">
        <div
          v-for="meeting in displayedMeetings"
          :key="meeting.id"
          @click="$emit('meeting-selected', meeting)"
          class="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors duration-200 relative"
          :class="getMeetingClasses(meeting)"
        >
          <!-- Custom Badge Slot -->
          <slot name="meeting-badge" :meeting="meeting"></slot>

          <div class="flex justify-between items-start">
            <div class="flex-1">
              <h4 class="font-medium text-gray-900">{{ meeting.subject || 'Untitled Meeting' }}</h4>
              <div class="mt-1 text-sm text-gray-600">
                <div class="flex items-center gap-4">
                  <span class="flex items-center gap-1">
                    {{ formatDate(meeting.start?.dateTime) }}
                  </span>
                  <span class="flex items-center gap-1">
                    <FontAwesomeIcon :icon="['fas', 'clock']" class="h-3 w-3" />
                    {{ formatTime(meeting.start?.dateTime) }} - {{ formatTime(meeting.end?.dateTime) }}
                  </span>
                </div>
                <div class="mt-1 flex items-center gap-4">
                  <span class="flex items-center gap-1">
                    <FontAwesomeIcon :icon="['fas', 'users']" class="h-3 w-3" />
                    {{ meeting.attendees?.length || 0 }} attendees
                  </span>
                  <span v-if="meeting.isCancelled" class="text-red-600 flex items-center gap-1">
                    <FontAwesomeIcon :icon="['fas', 'ban']" class="h-3 w-3" />
                    Cancelled
                  </span>
                </div>
              </div>
            </div>
            <div class="ml-4 flex flex-col items-end gap-1">
              <!-- Transcript Status -->
              <div v-if="meeting.transcriptChecked" class="flex items-center gap-1">
                <div
                  v-if="meeting.hasTranscript === true"
                  class="text-green-600 flex items-center gap-1"
                  title="Transcript available"
                >
                  <FontAwesomeIcon :icon="['fas', 'file-alt']" class="h-4 w-4" />
                  <span class="text-xs">Transcript</span>
                </div>
                <div
                  v-else
                  class="text-gray-400 flex items-center gap-1"
                  title="No transcript available"
                >
                  <FontAwesomeIcon :icon="['fas', 'file-alt']" class="h-4 w-4" />
                  <span class="text-xs">No transcript</span>
                </div>
              </div>

              <!-- Check Transcript Button -->
              <button
                v-if="!meeting.transcriptChecked && meeting.onlineMeeting?.joinUrl && !disableTranscriptCheck(meeting)"
                @click.stop="checkTranscript(meeting)"
                :disabled="meeting.checkingTranscript"
                class="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="meeting.checkingTranscript" class="flex items-center gap-1">
                  <svg class="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Checking...
                </span>
                <span v-else>Check Transcript</span>
              </button>

              <!-- No online meeting indicator -->
              <div
                v-if="!meeting.onlineMeeting?.joinUrl"
                class="text-gray-400 text-xs"
                title="Not an online meeting"
              >
                No recording
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';
import {mapGetters} from "vuex";
import { formatDate, formatTime } from '@/utils/common';

export default defineComponent({
  name: 'TeamsMeetingList',
  components: {
    FontAwesomeIcon,
    LoadingIndicator
  },
  props: {
    appUserId: {
      type: [Number, String],
      default: null
    },
    filterPredicate: {
      type: Function,
      default: null
    },
    startDate:{
      type: String,
      default: null
    },
    endDate:{
      type: String,
      default: null
    },
    emptyStateMessage: {
      type: String,
      default: 'No meetings found in the selected date range'
    },
    meetingClassFunction: {
      type: Function,
      default: null
    },
    disableTranscriptCheck: {
      type: Function,
      default: () => false
    }
  },
  emits: ['meeting-selected', 'meetings-loaded'],
  data() {
    return {
      loading: false,
      error: null,
      errorType: null, // 'auth', 'network', or 'general'
      meetings: [],
      hasEverLoadedSuccessfully: false,
      dateRange: {
        start: '',
        end: ''
      }
    };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'current_user']),
    displayedMeetings() {
      let meetings = this.meetings;
      if (this.filterPredicate) {
        meetings = meetings.filter(this.filterPredicate);
      }
      // Sort meetings in reverse chronological order by start date/time
      return meetings.slice().sort((a, b) => {
        const aTime = new Date(a.start?.dateTime).getTime();
        const bTime = new Date(b.start?.dateTime).getTime();
        return bTime - aTime;
      });
    },
    effectiveAppUserId() {
      // Use prop if provided, otherwise fall back to current user
      return this.appUserId || this.current_user?.id;
    }
  },
  mounted() {
    this.setDateRangePreset();
  },
  methods: {
    getMeetingClasses(meeting) {
      if (this.meetingClassFunction) {
        return this.meetingClassFunction(meeting);
      }
      return {};
    },

    setDateRangePreset() {
      if (this.startDate) {
        // If startDate prop is provided, use it
        this.dateRange.start = this.startDate;
      } else {
        const start = new Date();
        start.setDate(start.getDate() - 30);
        this.dateRange.start = start.toISOString().split('T')[0];
      }

      if (this.endDate) {
        this.dateRange.end = this.endDate;
      } else {
        const end = new Date();
        end.setDate(end.getDate() + 1);
        this.dateRange.end = end.toISOString().split('T')[0];
      }

      this.fetchMeetings();
    },

    async fetchMeetings() {
      // Don't fetch if no user ID is available
      if (!this.effectiveAppUserId) {
        this.meetings = [];
        return;
      }

      this.loading = true;
      this.error = null;
      this.errorType = null;

      try {
        const response = await axios.get('/api/microsoftteams/meetings/', {
          params: {
            appuser_id: this.effectiveAppUserId,
            start_date: this.dateRange.start,
            end_date: this.dateRange.end
          }
        });

        this.meetings = response.data || [];

        // Initialize transcript status as unknown for all meetings
        this.meetings.forEach(meeting => {
          meeting.hasTranscript = null; // null means not checked yet
          meeting.transcriptChecked = false;
          meeting.checkingTranscript = false;
        });

        // Mark that we've successfully loaded at least once
        this.hasEverLoadedSuccessfully = true;

        // Emit event so parent can process meetings if needed
        this.$emit('meetings-loaded', this.meetings);

      } catch (error) {
        console.error('Error fetching meetings:', error);

        // Determine error type based on response
        if (error.response) {
          const status = error.response.status;
          const errorMessage = error.response.data?.error || '';

          if (status === 400 && errorMessage.toLowerCase().includes('token')) {
            this.errorType = 'auth';
            this.error = 'Unable to access Microsoft Teams for this user.';
          } else if (status >= 500) {
            this.errorType = 'general';
            this.error = 'Server error. Please try again later.';
          } else {
            this.errorType = 'general';
            this.error = errorMessage || 'Failed to load meetings. Please try again.';
          }
        } else if (error.request) {
          // Request was made but no response received
          this.errorType = 'network';
          this.error = 'Network error. Please check your connection.';
        } else {
          // Something else happened
          this.errorType = 'general';
          this.error = 'An unexpected error occurred. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },

    async checkTranscript(meeting) {
      // Don't check if already checking or if no online meeting
      if (meeting.checkingTranscript || !meeting.onlineMeeting?.joinUrl) {
        return;
      }

      meeting.checkingTranscript = true;

      try {
        // Call API to check this specific meeting for transcript
        const response = await axios.get('/api/microsoftteams/meetings/', {
          params: {
            appuser_id: this.effectiveAppUserId,
            event_id: meeting.id
          }
        });

        // Check if any online meetings have transcripts
        const hasTranscript = response.data.online_meeting_data?.some(
          om => om.transcript_available === true
        );

        meeting.hasTranscript = hasTranscript;
        meeting.transcriptChecked = true;

        // Store the online meeting ID if transcript is available
        if (hasTranscript) {
          const meetingWithTranscript = response.data.online_meeting_data.find(
            om => om.transcript_available === true
          );
          meeting.onlineMeetingId = meetingWithTranscript?.online_meeting_id;
        }

      } catch (error) {
        console.error('Error checking transcript:', error);
        meeting.hasTranscript = false;
        meeting.transcriptChecked = true;
      } finally {
        meeting.checkingTranscript = false;
      }
    },
    formatDate,
    formatTime,
  }
});
</script>
